import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Landing from "@/pages/Landing";
import Intake from "@/pages/Intake";
import Generating from "@/pages/Generating";
import Packet from "@/pages/Packet";
import Discernment from "@/pages/Discernment";
import ResonancePackage from "@/pages/ResonancePackage";
import Progress from "@/pages/Progress";
import ProgressPacket from "@/pages/ProgressPacket";
import Chat from "@/pages/Chat";
import Community from "@/pages/Community";
import Library from "@/pages/Library";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { MusicPlayerProvider } from "@/context/MusicPlayerContext";
import Analytics from "@/components/Analytics";
import DisclaimerGate from "@/components/DisclaimerGate";
import SiteFooter from "@/components/SiteFooter";
import MusicPlayer from "@/components/MusicPlayer";
import "@/App.css";

// Capture engine override (?engine=local | ?engine=cloud) on first load
// of any route so it survives navigation across the app.
function useCaptureEngineOverride() {
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const e = (p.get("engine") || "").toLowerCase();
      if (e === "local" || e === "cloud") {
        sessionStorage.setItem("rm_engine_override", e);
      }
    } catch { /* ignore */ }
  }, []);
}

// Reset scroll to the top of the page on every route change. React Router
// preserves the previous page's scroll position by default, which made
// e.g. /progress open halfway down the viewport when the user had been
// scrolled on the landing page.
//
// Three layers of defense:
//   1. Disable the browser's built-in scroll-restoration on back/forward
//      so the browser doesn't fight us (Chrome / Safari try to "be
//      helpful" and restore the prior scroll).
//   2. On every pathname change, scroll BOTH the window AND any direct
//      scrollable child (some pages use a flex-wrapped layout where the
//      window itself never scrolls). This handles the long Chat / Library
//      pages too.
//   3. Use `requestAnimationFrame` so the scroll fires AFTER React has
//      rendered the new page — otherwise on slow connections we'd
//      scroll BEFORE the page exists.
function ScrollToTop() {
  const { pathname } = useLocation();

  // Disable browser scroll restoration once on mount.
  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      try {
        window.history.scrollRestoration = "manual";
      } catch { /* read-only in some embedded browsers — ignore */ }
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // /library/<id> is the deep-link route; the Library page itself
    // smooth-scrolls to and highlights the targeted card. Leave that
    // alone so we don't scroll the user back to the top mid-animation.
    if (pathname.startsWith("/library/") && pathname.split("/").length > 2) {
      return;
    }
    const fire = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };
    // Fire immediately AND on the next animation frame, so we beat any
    // browser-level restore that runs slightly after React's render.
    fire();
    const raf = requestAnimationFrame(fire);
    return () => cancelAnimationFrame(raf);
  }, [pathname]);
  return null;
}

function App() {
  useCaptureEngineOverride();
  return (
    <HelmetProvider>
      <div className="App rm-grain min-h-screen relative overflow-hidden" data-testid="app-root">
        <div className="rm-cosmos" aria-hidden="true" />
        <BrowserRouter>
          <ScrollToTop />
          <Analytics />
          <AdminAuthProvider>
            <MusicPlayerProvider>
              <MusicPlayer />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/intake" element={<DisclaimerGate><Intake /></DisclaimerGate>} />
                <Route path="/generating" element={<Generating />} />
                <Route path="/packet/:id" element={<Packet />} />
                <Route path="/discernment" element={<DisclaimerGate><Discernment /></DisclaimerGate>} />
                <Route path="/discernment/:id" element={<ResonancePackage />} />
                <Route path="/progress" element={<DisclaimerGate><Progress /></DisclaimerGate>} />
                <Route path="/progress/:id" element={<ProgressPacket />} />
                <Route path="/chat" element={<DisclaimerGate><Chat /></DisclaimerGate>} />
                <Route path="/community" element={<Community />} />
                <Route path="/library" element={<Library />} />
                <Route path="/library/:id" element={<Library />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
              <SiteFooter />
            </MusicPlayerProvider>
          </AdminAuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;

